import { useMemo } from "react"
import { Table } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import {
  TrainingRecord,
  TrainingRecordTableProps,
} from "../types"
import { TableActionColumn } from "@/components/TableActionColumn"
import { ID } from "@/types/common"
import dayjs from "dayjs"

const TrainingRecordTable = ({
  trainingRecords,
  onEdit,
  onDelete,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  pagination,
}: TrainingRecordTableProps) => {
  const columns: ColumnsType<TrainingRecord> = useMemo(
    () => {
      const baseColumns: ColumnsType<TrainingRecord> = [
        {
          title: "训练日期",
          dataIndex: "trainingDate",
          key: "trainingDate",
          width: 120,
          render: (date: string) =>
            date ? dayjs(date).format("YYYY-MM-DD") : "-",
          sorter: (a, b) => {
            if (!a.trainingDate && !b.trainingDate) return 0
            if (!a.trainingDate) return 1
            if (!b.trainingDate) return -1
            return (
              dayjs(a.trainingDate).unix() - dayjs(b.trainingDate).unix()
            )
          },
        },
        {
          title: "辅助类型",
          dataIndex: "assistTypeName",
          key: "assistTypeName",
          width: 150,
          ellipsis: true,
          render: (name?: string) => name || "-",
          sorter: (a, b) =>
            (a.assistTypeName || "").localeCompare(
              b.assistTypeName || "",
              "zh",
            ),
        },
        {
          title: "记录人",
          dataIndex: "userName",
          key: "userName",
          width: 120,
          ellipsis: true,
          render: (name?: string) => name || "-",
          sorter: (a, b) =>
            (a.userName || "").localeCompare(b.userName || "", "zh"),
        },
        {
          title: "备注",
          dataIndex: "note",
          key: "note",
          ellipsis: true,
          render: (note: string) => note || "-",
        },
      ]

      if (onEdit || onDelete) {
        baseColumns.push(
          TableActionColumn<TrainingRecord>({
            onEdit,
            onDelete,
            deleteConfirmDescription: "确定要删除这条训练记录吗？",
            width: 150,
          }) as any,
        )
      }

      return baseColumns
    },
    [onEdit, onDelete],
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
        getCheckboxProps: (record: TrainingRecord) => ({
          name: record.id.toString(),
        }),
      }
    : undefined

  const tablePagination = pagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total: number, range: [number, number]) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }
    : {
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total: number, range: [number, number]) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }

  return (
    <Table
      columns={columns}
      dataSource={trainingRecords}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      pagination={tablePagination}
      size="middle"
      scroll={{ x: 1000 }}
      showSorterTooltip={false}
    />
  )
}

export default TrainingRecordTable
